import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { ActionRow } from '@/components/patterns/action-row'
import { Skeleton } from '@/components/ui/skeleton'
import { Typography } from '@/components/ui/typography'
import dayjs from '@/lib/dayjs'
import {
  useCreateSalesDemoMutation,
  useDeactivateSalesDemoMutation,
  useExtendSalesDemoMutation,
  useGenerateSalesDemoLoginCredentialsMutation,
  useReactivateSalesDemoMutation,
  useRetrySalesDemoMutation,
} from '@/lib/services/sales-demos/sales-demos.query'
import { isSalesDemoProvisioning } from '@/lib/services/sales-demos/sales-demos.utils'
import { getErrorMessage } from '@/lib/utils'
import {
  Check,
  Copy,
  ExternalLink,
  FlaskConical,
  KeyRound,
  RefreshCw,
  TriangleAlert,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

const statusPresentation = {
  active: { label: 'Ready', variant: 'success' },
  failed: { label: 'Needs attention', variant: 'destructive' },
  inactive: { label: 'Inactive', variant: 'muted' },
  pending: { label: 'Queued', variant: 'warning' },
  provisioning: { label: 'Building', variant: 'info' },
}

const stageLabels = {
  dataset: 'Adding realistic products, customers, and order history',
  ready: 'Ready for a sales conversation',
  stripe_account: 'Creating a unique Stripe test account',
  stripe_activation: 'Waiting for Stripe to enable card payments',
  test_payment: 'Creating the refundable Stripe test order',
  user: 'Creating the florist login',
}

function formatDate(value, timezone) {
  if (!value) return null

  const date = dayjs.utc(value)

  return date.isValid() ? date.tz(timezone).format('MMM D, YYYY [at] h:mm A z') : null
}

function loginCredentialsText(credentials) {
  return [
    `URL: ${credentials.login_url}`,
    `Email: ${credentials.email}`,
    `Password: ${credentials.password}`,
  ].join('\n')
}

async function copyText(value, successMessage) {
  try {
    await navigator.clipboard.writeText(value)
    toast.success(successMessage)
  } catch {
    toast.error('Could not copy to the clipboard.')
  }
}

function ProvisioningState({ salesDemo }) {
  const stageLabel = stageLabels[salesDemo.provisioning_stage]

  return (
    <Alert variant="info">
      <RefreshCw className="animate-spin" />
      <AlertTitle>Preparing the demo</AlertTitle>
      <AlertDescription>
        <p>{stageLabel ?? 'Waiting for the provisioning worker'}</p>
        <p>You can leave this page while it finishes.</p>
      </AlertDescription>
    </Alert>
  )
}

function CredentialCopyRow({ label, value, withBorder = false }) {
  return (
    <ActionRow
      padding="compact"
      radius="none"
      className={`gap-3 ${withBorder ? 'border-t border-border/60' : ''}`}
      aria-label={`Copy ${label}`}
      onClick={() => copyText(value, `${label} copied.`)}
    >
      <span className="min-w-0 flex-1">
        <Typography variant="eyebrow" className="block">
          {label}
        </Typography>
        <Typography variant="supporting" tone="default" className="block break-all">
          {value}
        </Typography>
      </span>
      <Copy className="size-4 shrink-0" />
    </ActionRow>
  )
}

function LoginCredentials({ credentials }) {
  const fields = [
    { label: 'URL', value: credentials.login_url },
    { label: 'Email', value: credentials.email },
    { label: 'Password', value: credentials.password },
  ]

  return (
    <Alert variant="success">
      <Check />
      <AlertTitle>Demo login ready</AlertTitle>
      <AlertDescription>
        <p>This password is shown once. Copy it before leaving or refreshing this page.</p>
        <div className="overflow-hidden rounded-md border border-border/60">
          {fields.map((field, index) => (
            <CredentialCopyRow key={field.label} {...field} withBorder={index > 0} />
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            onClick={() =>
              copyText(loginCredentialsText(credentials), 'Demo login details copied.')
            }
          >
            <Copy />
            Copy login details
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  )
}

export default function OrganizationSalesDemoSection({ organizationId, salesDemoQuery, timezone }) {
  const [loginCredentials, setLoginCredentials] = useState(null)
  const createMutation = useCreateSalesDemoMutation()
  const retryMutation = useRetrySalesDemoMutation()
  const loginCredentialsMutation = useGenerateSalesDemoLoginCredentialsMutation()
  const extendMutation = useExtendSalesDemoMutation()
  const deactivateMutation = useDeactivateSalesDemoMutation()
  const reactivateMutation = useReactivateSalesDemoMutation()
  const salesDemo = salesDemoQuery.data?.sales_demo
  const featureEnabled = salesDemoQuery.data?.enabled === true
  const lifetimeDays = salesDemoQuery.data?.lifetime_days ?? 10
  const lifetimeLabel = `${lifetimeDays} ${lifetimeDays === 1 ? 'day' : 'days'}`
  const status = statusPresentation[salesDemo?.status] ?? statusPresentation.pending

  const runMutation = async (mutation, successMessage) => {
    try {
      await mutation.mutateAsync(organizationId)
      setLoginCredentials(null)
      toast.success(successMessage)
    } catch (error) {
      toast.error(getErrorMessage(error))
    }
  }

  const generateLoginCredentials = async () => {
    try {
      const response = await loginCredentialsMutation.mutateAsync(organizationId)
      setLoginCredentials(response.login_credentials)
      toast.success('Demo login generated.')
    } catch (error) {
      toast.error(getErrorMessage(error))
    }
  }

  if (salesDemoQuery.isPending) {
    return <Skeleton className="h-64 w-full rounded-xl" />
  }

  if (salesDemoQuery.isError || (!featureEnabled && !salesDemo)) {
    return null
  }

  return (
    <Card size="sm" surface="elevated">
      <CardHeader size="sm">
        <CardTitle size="sm" className="flex items-center gap-2">
          <FlaskConical className="size-5 text-brand-amethyst" />
          <span>Sales Demo</span>
        </CardTitle>
        <CardDescription size="sm">
          Build an isolated shop with 90 days of sample activity and its own Stripe test account.
        </CardDescription>
        {salesDemo ? (
          <CardAction>
            <Badge variant={status.variant}>{status.label}</Badge>
          </CardAction>
        ) : null}
      </CardHeader>

      <CardContent size="sm" className="space-y-4">
        {!salesDemo ? (
          <>
            <Typography variant="supporting" className="block">
              Available only for a newly created, empty florist. Provisioning runs in the background
              and can safely be retried.
            </Typography>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button disabled={createMutation.isPending}>
                  <FlaskConical />
                  {createMutation.isPending ? 'Starting…' : 'Create sales demo'}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Create a sales demo?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will turn this organization into a sales demo, create its own Stripe test
                    account, and add 90 days of sample products, customers, orders, and financial
                    activity. This organization should not be used as a real florist account.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => runMutation(createMutation, 'Sales demo provisioning started.')}
                    disabled={createMutation.isPending}
                  >
                    {createMutation.isPending ? 'Starting…' : 'Create demo'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        ) : null}

        {isSalesDemoProvisioning(salesDemo) ? <ProvisioningState salesDemo={salesDemo} /> : null}

        {salesDemo?.status === 'failed' ? (
          <Alert variant="destructive">
            <TriangleAlert />
            <AlertTitle>Provisioning stopped</AlertTitle>
            <AlertDescription>
              <p>{salesDemo.last_error || 'The demo could not be prepared.'}</p>
              <div className="flex flex-wrap gap-2 pt-2">
                <Button
                  size="sm"
                  onClick={() => runMutation(retryMutation, 'Sales demo retry started.')}
                  disabled={retryMutation.isPending}
                >
                  <RefreshCw />
                  Retry provisioning
                </Button>
                {salesDemo.last_error?.startsWith(
                  'Stripe Connect needs one-time platform setup'
                ) ? (
                  <Button asChild size="sm" variant="outline">
                    <a
                      href="https://dashboard.stripe.com/settings/connect/platform-profile"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <ExternalLink />
                      Open Stripe platform profile
                    </a>
                  </Button>
                ) : null}
              </div>
            </AlertDescription>
          </Alert>
        ) : null}

        {salesDemo?.status === 'active' ? (
          <>
            <Card size="sm" surface="subtle">
              <CardHeader size="sm">
                <CardTitle size="xs">Ready to share</CardTitle>
                {salesDemo.stripe_ready ? (
                  <CardAction>
                    <Badge variant="successSoft">Stripe test payments ready</Badge>
                  </CardAction>
                ) : (
                  <CardAction>
                    <Badge variant="warningSoft">Stripe readiness pending</Badge>
                  </CardAction>
                )}
              </CardHeader>
              <CardContent size="sm" className="space-y-1">
                <Typography variant="supporting" className="block break-all">
                  {salesDemo.primary_user?.email}
                </Typography>
                <Typography variant="caption" className="block">
                  Access ends {formatDate(salesDemo.expires_at, timezone)}.
                </Typography>
                <Typography variant="caption" className="block">
                  This demo includes sample FSN and Condolences orders so staff can explore those
                  workflows. These samples stay in OneStem and are not sent to FSN or Condolences.
                </Typography>
                <Typography variant="caption" className="block">
                  Generating a login replaces the current password. The new password is shown once.
                </Typography>
              </CardContent>
            </Card>

            {loginCredentials ? <LoginCredentials credentials={loginCredentials} /> : null}

            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={generateLoginCredentials}
                disabled={loginCredentialsMutation.isPending}
              >
                <KeyRound />
                {loginCredentialsMutation.isPending
                  ? 'Generating…'
                  : loginCredentials
                    ? 'Generate new password'
                    : 'Generate demo login'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  runMutation(extendMutation, `Demo access extended by ${lifetimeLabel}.`)
                }
                disabled={extendMutation.isPending}
              >
                Extend {lifetimeLabel}
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="outline">
                    Deactivate
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Deactivate this sales demo?</AlertDialogTitle>
                    <AlertDialogDescription>
                      The florist will immediately lose access. Its data and Stripe test account
                      stay available for later reactivation.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Keep active</AlertDialogCancel>
                    <AlertDialogAction
                      variant="destructive"
                      onClick={() => runMutation(deactivateMutation, 'Sales demo deactivated.')}
                    >
                      Deactivate
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </>
        ) : null}

        {salesDemo?.status === 'inactive' ? (
          <Alert variant="neutral">
            <AlertTitle>Demo access is inactive</AlertTitle>
            <AlertDescription>
              <p>
                {salesDemo.deactivation_reason === 'expired'
                  ? 'This demo reached its expiration date.'
                  : 'A OneStem team member deactivated this demo.'}{' '}
                Reactivating preserves the existing sample data and adds {lifetimeLabel}.
              </p>
              <Button
                size="sm"
                className="mt-2"
                onClick={() => runMutation(reactivateMutation, 'Sales demo reactivated.')}
                disabled={reactivateMutation.isPending}
              >
                Reactivate for {lifetimeLabel}
              </Button>
            </AlertDescription>
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  )
}
